import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './i18n';
import './global.css';
import './styles/lobby.css';
import './styles/blockjam.css';
import './styles/ui.css';
import './styles/watersort.css';
// Module 33 — tool-managed applied backgrounds; imported LAST so its rules
// override the hand-authored defaults above by source order.
import './styles/backgrounds.applied.css';
import { PluginRegistry } from '@puzzlemania/plugins';
import { initLayoutMode } from '@puzzlemania/ui';
import { createWaterSortManifest } from '@puzzlemania/watersort';
import { Logger } from '@puzzlemania/shared';
import { PlatformServicesProvider } from './composition/PlatformServicesContext';
import { createConfiguredBlockJamManifest } from './composition/blockjamManifest';
import { installBlockJamTopBarVars } from './composition/topBarVars';
import { installUserActivation } from './composition/userActivation';
import { ErrorBoundary } from './observability/ErrorBoundary';
import { RootErrorFallback } from './observability/RootErrorFallback';
import { registerGlobalErrorHandlers } from './observability/globalHandlers';
import { installHardwareBackHandler } from './lib/hardwareBack';

// WP-36.2 — bound to the config the build shipped (games/blockjam/config/*.json)
// rather than the library's in-bundle defaults, so the Level Studio's "Write
// config" actually retunes the game.
PluginRegistry.register(createConfiguredBlockJamManifest());
PluginRegistry.register(createWaterSortManifest());

// Module 57 — before anything that can await. The player's first gesture is the
// only thing that lets audio start, and the listeners used to be registered
// inside the composition root; anything they missed left the background music
// owed until the next gesture.
installUserActivation();

// Module 28 (WP-NAV.10) — Android hardware Back closes the topmost shared popup
// instead of navigating; no-op on web.
installHardwareBackHandler();

// Module 32 (WP-UI.1) — publish the app-wide `data-layout` signal (portrait/landscape)
// on the document root before first render so CSS + HUDs + Pixi read one source of truth.
initLayoutMode();

// Module 54 (WP-54.3) — publish Block Jam's Top Bar chrome (`--bj-topbar-*`) from
// its config, likewise before first render: `.app-top-bar.bj-topbar` consumes the
// properties with no fallback, so the bar has nothing to paint until they exist.
installBlockJamTopBarVars();

// One instance for the whole app load — the root ErrorBoundary, the global
// window handlers, and (via PlatformServicesProvider) every game-plugin
// ErrorBoundary all share this same Logger, not a new one each.
const logger = new Logger(import.meta.env.DEV);
registerGlobalErrorHandlers(logger);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <ErrorBoundary logger={logger} fallback={<RootErrorFallback />}>
      <Suspense>
        <PlatformServicesProvider logger={logger}>
          <App />
        </PlatformServicesProvider>
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>,
);
